/**
 * The engines the New Connection dialog offers, read from the engine registry (Option C, C0).
 *
 * Every user-facing engine carries `connectionPicker` metadata in `engineDefinitions.ts`; `mock` has none and is never
 * listed (`USER_FACING_ENGINE_IDS` excludes it). Today's tile row reads its labels from here; C1's start view and rail
 * read the groups, blurbs and order.
 */
import { ENGINE_REGISTRY, USER_FACING_ENGINE_IDS } from '@/core/engines';
import type { ConnectionPickerGroup, ConnectionPickerInfo, UserFacingEngineId } from '@/core/engines';

export type EngineCatalogEntry = ConnectionPickerInfo & { readonly id: UserFacingEngineId };

function buildCatalog(): readonly EngineCatalogEntry[] {
  return USER_FACING_ENGINE_IDS.map((id) => {
    const picker = ENGINE_REGISTRY[id].connectionPicker;
    if (!picker) throw new Error(`Engine "${id}" has no connectionPicker metadata`);
    return { id, ...picker };
  }).sort((a, b) => a.order - b.order);
}

/** Every offered engine, in the dialog's order (Databases first, then Azure services). */
export const ENGINE_CATALOG: readonly EngineCatalogEntry[] = buildCatalog();

/** The entry for an engine id, or undefined for one the dialog does not offer (`mock`, in an e2e edit dialog). */
export function findEngineCatalogEntry(id: string): EngineCatalogEntry | undefined {
  return ENGINE_CATALOG.find((candidate) => candidate.id === id);
}

export function engineCatalogEntry(id: UserFacingEngineId): EngineCatalogEntry {
  const entry = ENGINE_CATALOG.find((candidate) => candidate.id === id);
  if (!entry) throw new Error(`Engine "${id}" is not in the connection catalog`);
  return entry;
}

/**
 * E2E performance trace only (Option C C1b, plan §3.2 "Performance": 12–16 cards). In test mode,
 * `localStorage['sql-studio-e2e-synthetic-engine-cards'] = N` adds N synthetic Databases cards (and rail items) after
 * the real ones, so the transition can be timed with more cards than the product has. They are not engines: a test
 * never picks one, and nothing outside the start view and the rail lists them.
 */
export const SYNTHETIC_CARDS_STORAGE_KEY = 'sql-studio-e2e-synthetic-engine-cards';

function syntheticEntries(): EngineCatalogEntry[] {
  if (typeof window === 'undefined' || window.electronAPI?.testMode?.isTestMode?.() !== true) return [];
  let count = 0;
  try {
    count = Math.min(16, Math.max(0, Number.parseInt(window.localStorage.getItem(SYNTHETIC_CARDS_STORAGE_KEY) ?? '0', 10) || 0));
  } catch {
    return [];
  }
  return Array.from({ length: count }, (_, index) => ({
    id: `synthetic-${index + 1}` as UserFacingEngineId,
    label: `Synthetic engine ${index + 1}`,
    group: 'databases' as const,
    blurb: 'A card for the transition performance trace',
    summary: 'Synthetic',
    keywords: 'synthetic',
    order: 100 + index,
  }));
}

/** The engines the start view and the rail list: the catalog, plus the e2e trace's synthetic cards in test mode. */
export function offeredEngines(): readonly EngineCatalogEntry[] {
  const synthetic = syntheticEntries();
  return synthetic.length > 0 ? [...ENGINE_CATALOG, ...synthetic] : ENGINE_CATALOG;
}

export function engineCatalogGroup(group: ConnectionPickerGroup): readonly EngineCatalogEntry[] {
  return offeredEngines().filter((entry) => entry.group === group);
}
